import SplitPaneOriginal, {
  SplitPaneProps,
  SplitPaneState,
} from 'react-split-pane';

declare global {
  interface Document {
    selection?: { empty: () => void };
  }
}

/**
 * This code is a modified version of what exists in react-split-pane to fix a
 * bug where moving cursor quickly off screen makes the resize bar get out of sync
 *
 * PR that would fix it if merged https://github.com/tomkp/react-split-pane/pull/522
 */
function unFocus() {
  if (document.selection) {
    document.selection.empty();
  } else {
    try {
      window.getSelection()?.removeAllRanges();
    } catch (e) {} // eslint-disable-line no-empty
  }
}

export class SplitPane extends SplitPaneOriginal {
  // State is initialized in SplitPaneOriginal
  state!: SplitPaneState & { position: number };
  props!: SplitPaneProps & {
    minExpandedSize?: number;
    children: React.ReactNode;
  };
  pane1: HTMLDivElement | undefined;
  pane2: HTMLDivElement | undefined;
  splitPane!: HTMLDivElement;
  onTouchMove(event: TouchEvent & { touches: MinLengthArray<1, Touch> }) {
    const {
      allowResize,
      maxSize,
      minSize,
      minExpandedSize,
      onChange,
      split,
      step,
    } = this.props as typeof this.props;
    const { active, position } = this.state;

    if (allowResize && active) {
      unFocus();
      const isPrimaryFirst = this.props.primary === 'first';

      const pane1: HTMLDivElement | undefined = this.pane1;
      const pane2: HTMLDivElement | undefined = this.pane2;

      const ref = isPrimaryFirst ? pane1 : pane2;
      const ref2 = isPrimaryFirst ? pane2 : pane1;
      if (ref && ref2) {
        const node = ref;
        const node2 = ref2;

        if (node.getBoundingClientRect) {
          const width = node.getBoundingClientRect().width;
          const height = node.getBoundingClientRect().height;
          const current =
            split === 'vertical'
              ? event.touches[0].clientX
              : event.touches[0].clientY;
          const size = split === 'vertical' ? width : height;
          let positionDelta = position - current;
          if (step) {
            if (Math.abs(positionDelta) < step) {
              return;
            }
            // Integer division
            // eslint-disable-next-line no-bitwise
            positionDelta = ~~(positionDelta / step) * step;
          }
          let sizeDelta = isPrimaryFirst ? positionDelta : -positionDelta;

          const pane1Order = parseInt(window.getComputedStyle(node).order, 10);
          const pane2Order = parseInt(window.getComputedStyle(node2).order, 10);
          if (pane1Order > pane2Order) {
            sizeDelta = -sizeDelta;
          }

          let newMaxSize = maxSize;
          if (typeof maxSize === 'number' && maxSize <= 0) {
            const splitPane: HTMLDivElement = this.splitPane;
            if (split === 'vertical') {
              newMaxSize = splitPane.getBoundingClientRect().width + maxSize;
            } else {
              newMaxSize = splitPane.getBoundingClientRect().height + maxSize;
            }
          }

          let newSize = size - sizeDelta;
          let newPosition = position - positionDelta;

          if (typeof minSize === 'number' && newSize < minSize) {
            newPosition -= isPrimaryFirst
              ? newSize - minSize
              : minSize - newSize;
            newSize = minSize;
          } else if (
            typeof minSize === 'number' &&
            typeof minExpandedSize === 'number' &&
            newSize < minExpandedSize
          ) {
            // Are we closer to min or min expanded?
            if (
              Math.abs(newSize - minExpandedSize) < Math.abs(newSize - minSize)
            ) {
              newPosition -= isPrimaryFirst
                ? newSize - minExpandedSize
                : minExpandedSize - newSize;
              newSize = minExpandedSize;
            } else {
              newPosition -= isPrimaryFirst
                ? newSize - minSize
                : minSize - newSize;
              newSize = minSize;
            }
          } else if (typeof newMaxSize === 'number' && newSize > newMaxSize) {
            newPosition -= isPrimaryFirst
              ? newSize - newMaxSize
              : newMaxSize - newSize;
            newSize = newMaxSize;
          } else {
            this.setState({ resized: true });
          }

          if (onChange) onChange(newSize);

          this.setState({
            // @ts-expect-error position missing from react-split-pane types
            position: newPosition,
            draggedSize: newSize,
            [isPrimaryFirst ? 'pane1Size' : 'pane2Size']: newSize,
          });
        }
      }
    }
  }
}
